import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxNotifyService } from '@shared/services/ngx-notify/ngx-notify.service';
import { ResearcherService } from '../../services/researcher.service';
import { Researcher } from '@features/admin/researcher-management/models/researcher.model';

function atLeastOneRoleValidator(ctrl: AbstractControl): ValidationErrors | null {
  const g = ctrl as FormGroup;
  const anySelected = ['isResearchPersonnel','isPrincipalInvestigator','isNtrIrbCoordinator']
    .some(c => !!g.get(c)?.value);
  return anySelected ? null : { rolesRequired: true };
}

@Component({
  selector: 'app-researcher-edit',
  templateUrl: './researcher-edit.component.html',
  styleUrls: ['./researcher-edit.component.scss']
})
export class ResearcherEditComponent implements OnInit {
  researcherForm!: FormGroup;
  isLoading = false;
  disclaimer = "";
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Researcher,
    private fb: FormBuilder,
    private researcherService: ResearcherService,
    private notify: NgxNotifyService,
    private dialogRef: MatDialogRef<ResearcherEditComponent>
  ) {}

  ngOnInit(): void {
    const activeRoles = (this.data.researcherRoles || []).filter(r => r.isActive).map(r => r.role);
    const isResearchPersonnel = activeRoles.includes('ResearchPersonnel'); // isActive fallback for old data
    const isPI = activeRoles.includes('PrincipalInvestigator');
    const isCoord = activeRoles.includes('NTRIRBCoordinator');
    this.researcherForm = this.fb.group({
      lastName: [this.data.lastName, [Validators.required, Validators.maxLength(100)]],
      firstName: [this.data.firstName, [Validators.required, Validators.maxLength(100)]],
      email: [this.data.email, [Validators.email, Validators.maxLength(255)]],
      phone: [this.data.phone, [Validators.pattern(/^\d{10}$/)]],
      isResearchPersonnel: [isResearchPersonnel],
      isPrincipalInvestigator: [isPI],
      isNtrIrbCoordinator: [isCoord]
    }, { validators: atLeastOneRoleValidator });
  }

  editResearcher(): void {
    if (this.researcherForm.invalid) {
      this.researcherForm.markAllAsTouched();
      if (this.researcherForm.errors?.rolesRequired) this.notify.error('Select at least one Role');
      else this.notify.error('Please fill in all required fields correctly');
      return;
    }
    const formData = this.researcherForm.value;
    const roles: string[] = [];
    if (formData.isResearchPersonnel) roles.push('ResearchPersonnel');
    if (formData.isPrincipalInvestigator) roles.push('PrincipalInvestigator');
    if (formData.isNtrIrbCoordinator) roles.push('NTRIRBCoordinator');
    const dto: any = {
      id: this.data.id,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email || null,
      phone: formData.phone || null,
      isActive: roles.length > 0,
      roles: roles
    };
    this.isLoading = true;
    this.researcherService.update(dto).subscribe({
      next: () => {
        this.notify.success('User updated successfully');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: err => {
        const msg = err?.error?.extendedMessage || 'Failed to update User';
        this.notify.error(msg);
        this.isLoading = false;
      }
    });
  }

  getErrorMessage(fieldName: string): string {
    const control = this.researcherForm.get(fieldName);
    if (control?.hasError('required')) return `${fieldName} is required`;
    if (control?.hasError('email')) return 'Please enter a valid email address';
    if (control?.hasError('pattern')) return 'Phone must be exactly 10 digits';
    if (control?.hasError('maxLength')) return `${fieldName} is too long`;
    return '';
  }
}
